import type { AtivacaoPedido, LinhaAtivacao } from '@/hooks/useAtivacaoLinhas';

export function pedidoInicialEsim<T extends { id: string; estado?: string }>(pedidos: T[]) {
  return pedidos.find(p => p.estado === 'em_andamento') || pedidos[0];
}

export function linhaEstaValidada(linha: Pick<LinhaAtivacao, 'validada_em' | 'revisao_validada' | 'revisao'>) {
  return !!linha.validada_em && linha.revisao_validada === linha.revisao;
}

/**
 * ⛔ 'enviando' NÃO É DECISÃO: é "estou mandando agora" (Douglas 22/09/2026).
 * A pergunta é reservada e commitada ANTES de sair; se o backend reinicia no
 * meio — e todo deploy reinicia — ela fica 'enviando' para sempre. Passados
 * estes minutos ninguém está mandando mais. Mesmo número do backend
 * (MINUTOS_ENVIO_TRAVADO em domain/qualidade/situacao-esim.ts): se um lado
 * mudar sem o outro, o botão aparece e o envio responde 409.
 */
export const MINUTOS_ENVIO_TRAVADO = 15;

export function envioEsimTravado(linha: Pick<LinhaAtivacao, 'confirmacao_status' | 'confirmacao_criado_em'>, agora = Date.now()) {
  if (linha.confirmacao_status === 'incerta') return true;
  if (linha.confirmacao_status !== 'enviando') return false;
  if (!linha.confirmacao_criado_em) return true;
  return agora - Date.parse(linha.confirmacao_criado_em) >= MINUTOS_ENVIO_TRAVADO * 60_000;
}

/**
 * A ajuda já pode ser encerrada pelo reenvio canônico após assumir/atender.
 * ⛔ E o reenvio não se trava (Douglas 22/09/2026): envio órfão ou não
 * confirmado deixa de segurar a pergunta nova — antes só o master destravava,
 * e o cliente ficava horas sem ninguém poder repetir.
 */
export function podeReenviarConfirmacao(linha: Pick<LinhaAtivacao, 'confirmacao_status' | 'confirmacao_criado_em'>, agora = Date.now()) {
  return ['enviada', 'ajuda', 'revisar'].includes(linha.confirmacao_status || '') || envioEsimTravado(linha, agora);
}

export function linhaProntaParaTroca(linha: LinhaAtivacao) {
  return linha.tipo === 'esim_simcard' && !linha.entrada_em && !linhaEstaValidada(linha)
    && !!linha.foto_mensagem_id && !!linha.iccid && !!linha.conferido_em;
}

/** Há quanto tempo o cliente está devendo o chip. Estado gravado, não rótulo. */
export function esperaChipEsim(linha: LinhaAtivacao) {
  if (!linha.aguardando_chip_em || linhaEstaValidada(linha)) return '';
  const horas = Math.max(0, Math.floor((Date.now() - Date.parse(linha.aguardando_chip_em)) / 3600000));
  const tempo = horas >= 48 ? `há ${Math.floor(horas / 24)} dias` : horas >= 1 ? `há ${horas}h` : 'agora há pouco';
  return linha.aguardando_chip_por ? `Cliente ainda não tem o chip · ${tempo}` : `Aguardando o chip do cliente · ${tempo}`;
}

/**
 * Onde a linha está no processo, com o nome que a operação usa. É o mesmo
 * texto na triagem e no Painel CRM: quem atende não precisa traduzir duas
 * telas diferentes para saber o que falta (Douglas 21/09/2026).
 */
export function situacaoLinhaEsim(l: LinhaAtivacao & { troca_status?: string | null; previsao_em?: string | null }, agora = Date.now()) {
  // Fonte única no backend; o cálculo abaixo só cobre resposta antiga em cache.
  if (l.situacao_rotulo) return l.situacao_rotulo;
  const hora = (v?: string | null) => v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
  if (linhaEstaValidada(l)) return 'Funcionando · validado';
  if (!l.tipo) return 'Falta dizer se é eSIM ou chip';
  if (l.confirmacao_status === 'ajuda') return 'Cliente pediu ajuda';
  if (l.confirmacao_status === 'revisar') return 'Resposta do cliente precisa de revisão';
  if (envioEsimTravado(l, agora)) return 'Envio não confirmou · confira a conversa e pergunte de novo';
  if (l.confirmacao_status === 'enviando') return 'Enviando a pergunta agora';
  if (l.tipo === 'esim_real') {
    if (!l.entrada_em) return 'Esperando a operadora concluir';
    return l.confirmacao_status ? 'Esperando o cliente confirmar que funciona'
      : 'Validação ainda não iniciada · ninguém perguntou ao cliente';
  }
  const espera = esperaChipEsim(l);
  if (espera) return espera;
  if (!l.foto_mensagem_id) return 'Esperando a foto do chip';
  if (!l.iccid || !l.conferido_em) return 'Falta conferir o ICCID da foto';
  if (!l.entrada_em) return 'Esperando a operadora concluir para pedir a troca';
  if (l.troca_status === 'encaminhada' && l.confirmacao_status === 'enviada' && l.confirmacao_criado_em
    && (!l.troca_criado_em || Date.parse(l.confirmacao_criado_em) >= Date.parse(l.troca_criado_em))) {
    return `Esperando o cliente dizer se funciona · perguntamos ${hora(l.confirmacao_criado_em)}`;
  }
  if (l.troca_status === 'encaminhada') return l.previsao_em && new Date(l.previsao_em).getTime() > agora
    ? `Claro fazendo a troca · prazo até ${hora(l.previsao_em)}`
    : 'Prazo da Claro venceu · perguntar se já funciona';
  if (l.troca_status === 'pendencia') return 'Gerente apontou pendência';
  if (l.troca_status === 'incerta') return 'Envio à gerente não confirmado';
  if (l.troca_status === 'enviando') return 'Enviando o pedido à gerente';
  if (l.troca_status === 'enviada') return 'Com a gerente, aguardando ela pedir à Claro';
  return 'Pronto para pedir a troca à gerente';
}

export function resumoLinhaEsim(linha: LinhaAtivacao) {
  if (!linha.tipo) return 'Classificar tipo de eSIM';
  if (!linha.entrada_em && !linhaEstaValidada(linha) && linha.tipo === 'esim_real') return 'Aguardando conclusão na operadora';
  // ⛔ "aguardando confirmação do cliente" valia para QUALQUER confirmacao_status,
  // inclusive envio órfão e não confirmado — e a tela mandava esperar uma
  // resposta a uma pergunta que ninguém sabia se tinha saído (Douglas 22/09/2026).
  // ⛔ "FUNCIONAMENTO CONFIRMADO" É PROVA DO CLIENTE (Douglas 23/09/2026).
  // A frase saía em QUALQUER linha validada — inclusive nas 2.602 liberadas por
  // exceção de master, onde ninguém confirmou nada, contra 181 confirmadas de
  // verdade. A tela dava por confirmado 93% do que o cliente nunca respondeu.
  if (linha.tipo === 'esim_real') return `Ativação no portal · ${linhaEstaValidada(linha)
      ? (linha.validacao_origem === 'cliente' ? 'funcionamento confirmado' : 'liberada por exceção · cliente não confirmou')
    : envioEsimTravado(linha) ? 'envio não confirmou · pergunte de novo'
    : linha.confirmacao_status ? 'aguardando confirmação do cliente' : 'validação não iniciada'}`;
  if (!linha.foto_mensagem_id) return linha.situacao_rotulo || esperaChipEsim(linha) || 'Aguardando foto do chip';
  if (!linha.iccid) return 'Conferir ICCID';
  return `ICCID: ${linha.iccid} · ${linha.conferido_em ? 'conferido' : 'conferir'}`;
}

export function andamentoTrocaEsim(linha: LinhaAtivacao) {
  if (linhaEstaValidada(linha)) return linha.validacao_origem === 'operadora'
    ? 'Troca concluída pela Claro (e-mail do setor de troca).' : 'Funcionamento confirmado.';
  if (linha.tipo !== 'esim_simcard') return '';
  if (linha.troca_status === 'incerta') return 'Envio não confirmado. Confira o e-mail antes de repetir.';
  if (linha.troca_status === 'enviando') return 'Enviando solicitação à gerente…';
  if (linha.troca_status === 'enviada') return 'E-mail aceito pelo provedor. Aguardando encaminhamento da gerente.';
  if (linha.troca_status === 'pendencia') return 'Gerente informou pendência. Abra a linha para tratar.';
  if (linha.troca_status === 'encaminhada') return linha.previsao_em
    ? `Troca encaminhada · previsão ${new Date(linha.previsao_em).toLocaleString('pt-BR')}. Falta confirmar com o cliente.`
    : 'Troca encaminhada. Falta confirmar com o cliente.';
  if (linha.troca_status === 'cancelada') return 'Solicitação anterior encerrada. Confira antes de enviar novamente.';
  if (!linha.foto_mensagem_id) return 'Próximo passo: vincular a foto recebida.';
  if (!linha.iccid || !linha.conferido_em) return 'Foto vinculada. Próximo passo: conferir e salvar o ICCID.';
  if (!linha.entrada_em) return 'Chip conferido. Aguardando conclusão na operadora para solicitar a troca.';
  return 'Chip conferido. Solicitação ainda não enviada. Abra a linha para enviar ou acompanhar a automação.';
}

export function reunirLinhasEsim(pedidos: Array<{ id: string; numero: string }>, familias: Array<AtivacaoPedido | undefined>) {
  const vistas = new Set<string>();
  return familias.flatMap((familia, indice) => (familia?.linhas || []).flatMap(linha => {
    if (vistas.has(linha.id)) return [];
    vistas.add(linha.id);
    return [{ ...linha, familiaId: pedidos[indice].id, pedidoNumero: pedidos[indice].numero }];
  }));
}

/**
 * "Em andamento" sozinho fazia o consultor apertar Conferência de novo achando
 * que nada tinha saído — a pergunta já estava com o cliente havia horas. O
 * rodapé passa a dizer QUANDO ela saiu (Douglas 22/09/2026). Data real gravada
 * na confirmação; sem data, não se inventa texto.
 */
export function andamentoConfirmacaoEsim(linhas: LinhaAtivacao[] | undefined, agora = Date.now()) {
  // ⛔ "Validação em andamento" era o que o rodapé dizia quando o envio tinha
  // ficado órfão: a pergunta nunca constou como enviada e ninguém sabia
  // (Douglas 22/09/2026). Envio travado vem ANTES de qualquer espera — não há
  // o que aguardar do cliente enquanto não se souber se a pergunta saiu.
  const travadas = (linhas || []).filter(l => !linhaEstaValidada(l) && envioEsimTravado(l, agora));
  if (travadas.length) {
    const reservada = travadas.map(l => l.confirmacao_criado_em).filter(Boolean).sort().pop();
    const quando = reservada ? ` de ${new Date(reservada).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : '';
    return `Pergunta${quando} sem envio confirmado · confira a conversa e pergunte de novo.`;
  }
  const espera = (linhas || []).filter(l => !linhaEstaValidada(l) && l.confirmacao_enviada_em
    && ['enviada', 'ajuda', 'revisar', 'incerta'].includes(l.confirmacao_status || ''));
  if (!espera.length) return '';
  const enviada = espera.reduce((a, b) => Date.parse(a.confirmacao_enviada_em!) >= Date.parse(b.confirmacao_enviada_em!) ? a : b);
  const quando = new Date(enviada.confirmacao_enviada_em!).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  if (espera.some(l => l.confirmacao_status === 'ajuda')) return `Pergunta enviada em ${quando} · o cliente pediu ajuda.`;
  if (espera.some(l => l.confirmacao_status === 'revisar')) return `Pergunta enviada em ${quando} · a resposta precisa de revisão.`;
  if (espera.some(l => l.confirmacao_status === 'incerta')) return `Pergunta de ${quando} sem envio confirmado · confira na conversa antes de repetir.`;
  return `Pergunta enviada em ${quando} · aguardando a resposta do cliente.`;
}

/**
 * A pergunta some no meio da conversa: o cliente fica em dúvida, manda áudio,
 * fala de outro assunto e nunca responde 1 ou 2. Reenviar já existia dentro da
 * linha, escondido; estes são os grupos que o rodapé oferece reenviar, com a
 * mesma régua de sempre — por TIPO de chip, porque a pergunta é uma só para
 * todas as linhas do mesmo tipo (Douglas 22/09/2026).
 */
export function gruposReenvioEsim(linhas: LinhaAtivacao[] | undefined) {
  const espera = (linhas || []).filter(l => !linhaEstaValidada(l) && !!l.entrada_em && !!l.tipo
    && podeReenviarConfirmacao(l));
  const grupos = new Map<string, LinhaAtivacao>();
  for (const l of espera) {
    const atual = grupos.get(l.tipo!);
    const recente = (v?: string | null) => v ? Date.parse(v) : 0;
    if (!atual || recente(l.confirmacao_enviada_em) > recente(atual.confirmacao_enviada_em)) grupos.set(l.tipo!, l);
  }
  return [...grupos.entries()].map(([tipo, linha]) => ({
    tipo, linha, quantas: espera.filter(l => l.tipo === tipo).length,
    rotulo: tipo === 'esim_real' ? 'eSIM REAL' : 'SIMCARD',
  }));
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
/**
 * Competência curta (ago/26). Vazio é vazio: não se inventa mês. A mesma régua
 * do banco (`^20\\d{2}-(0[1-9]|1[0-2])$`): ano fora do século é dado sujo da
 * origem — existe 1 item em '0202-02' — e viraria a competência mais antiga de
 * todas, furando a fila para sempre (Douglas 22/09/2026).
 */
export function rotuloCompetencia(competencia?: string | null) {
  const m = /^(20\d{2})-(0[1-9]|1[0-2])$/.exec(competencia || '');
  return m ? `${MESES[Number(m[2]) - 1]}/${m[1].slice(2)}` : '—';
}

/** Competência anterior à de hoje = trabalho atrasado, prioridade na fila. */
export function competenciaAtrasada(competencia?: string | null, agora = new Date()) {
  if (!/^20\d{2}-(0[1-9]|1[0-2])$/.test(competencia || '')) return false;
  const mes = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
  return (competencia as string) < mes;
}

/**
 * Onde cabe a pergunta de REAVALIAÇÃO: só com pedido de ajuda aberto, por tipo
 * de chip. É a mensagem que faltava no processo — depois de ajudar, alguém
 * precisa reperguntar, e com outro texto (Douglas 22/09/2026).
 */
export function gruposRevalidacaoEsim(linhas: LinhaAtivacao[] | undefined) {
  return gruposReenvioEsim((linhas || []).filter(l => l.confirmacao_status === 'ajuda'));
}

/** ⛔ "EM ANDAMENTO" É DO TRABALHO PENDENTE (Douglas 23/09/2026).
 * O modal contava QUALQUER `validada_em` como "já começou": validar 3 das 6
 * linhas por exceção de master fazia o botão "Iniciar validação" sumir e o
 * rodapé dizer "Validação em andamento" num pedido em que NENHUMA pergunta
 * saiu — zero linhas em `qualidade_ativacao_confirmacoes` e nenhuma mensagem
 * na conversa (pedido 2607130070, 19/09/2026). As 3 linhas restantes ficavam
 * sem porta: sem Iniciar e sem grupo de reenvio, porque reenvio precisa de uma
 * pergunta anterior.
 * Começou quando alguma linha AINDA PENDENTE tem pergunta, foto, ICCID ou
 * troca — ou quando alguma linha foi validada pela PROVA DO CLIENTE. A exceção
 * de master não é prova de que a validação começou; ela só encerra a linha. */
export function validacaoEsimComecou(linhas: LinhaAtivacao[] | undefined): boolean {
  return (linhas || []).some(l => linhaEstaValidada(l)
    ? (l.validacao_origem || '') !== 'master'
    : !!(l.confirmacao_status || l.foto_mensagem_id || l.conferido_em || l.troca_status));
}

/** Dígito verificador do ICCID (Luhn, ITU-T E.118). Aqui é AVISO, não bloqueio:
 * quem está com a etiqueta na mão pode decidir contra. No caminho automático da
 * IA é barreira — foi lá que um número com dois dígitos trocados passou. */
export function iccidFecha(valor: string): boolean {
  const limpo = valor.replace(/\D/g, '');
  if (!/^89\d{17,18}$/.test(limpo)) return false;
  let soma = 0;
  for (let i = 0; i < limpo.length; i++) {
    let d = Number(limpo[limpo.length - 1 - i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    soma += d;
  }
  return soma % 10 === 0;
}
